import React, { useMemo, useState } from 'react';
import { Search, Trash2 } from 'lucide-react';
import { useFetchPropertyResidentsQuery } from '@@/services/property';
import { translateResidentType } from '@@/constants/user';
import { normalizeText } from '@@/utils/string';
import DeletePropertyResidentModal from '@@/components/Modals/DeletePropertyResidentModal';

const stateCardClassName =
  'bg-white rounded-card shadow-card border border-neutral-200 p-8 text-center';

// The API returns the residents sorted by floor, apartment number and name,
// with only the account of this property
const getAccount = (resident: Alacaza.Resident) => resident.accounts[0];

const formatFloor = (floor?: number) => {
  if (floor === undefined || floor === null) {
    return '-';
  }
  return floor === 0 ? 'RDC' : String(floor);
};

const PropertyResidents: React.FC<{ propertyId: string }> = ({
  propertyId,
}) => {
  const {
    data: residents,
    isLoading,
    error,
  } = useFetchPropertyResidentsQuery(propertyId);
  const [search, setSearch] = useState('');
  const [residentToDelete, setResidentToDelete] =
    useState<Alacaza.Resident | null>(null);

  const searchIndex = useMemo(
    () =>
      (residents || []).map((resident) => ({
        resident,
        text: normalizeText(`${resident.firstName} ${resident.lastName}`),
      })),
    [residents],
  );

  // Every word must match, so that "Prénom Nom" and "Nom Prénom" both work
  const filteredResidents = useMemo(() => {
    const words = normalizeText(search.trim()).split(/\s+/).filter(Boolean);
    return searchIndex
      .filter(({ text }) => words.every((word) => text.includes(word)))
      .map(({ resident }) => resident);
  }, [searchIndex, search]);

  const content = useMemo(() => {
    if (isLoading) {
      return (
        <div className={stateCardClassName}>
          <p className="text-body text-neutral-500">Chargement...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className={stateCardClassName}>
          <p className="text-body text-danger-600">
            Impossible de charger les résidents
          </p>
        </div>
      );
    }

    if (!residents || residents.length === 0) {
      return (
        <div className={stateCardClassName}>
          <p className="text-body text-neutral-500">Aucun résident</p>
        </div>
      );
    }

    if (filteredResidents.length === 0) {
      return (
        <div className={stateCardClassName}>
          <p className="text-body text-neutral-500">
            Aucun résident ne correspond à « {search.trim()} »
          </p>
        </div>
      );
    }

    return (
      <>
        {/* Mobile Cards - visible < lg */}
        <div className="lg:hidden space-y-4">
          {filteredResidents.map((resident) => {
            const account = getAccount(resident);
            return (
              <div
                key={resident.id}
                className="bg-white rounded-card shadow-card border border-neutral-200 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-neutral-900 text-body mb-1">
                      {resident.firstName} {resident.lastName}
                    </p>
                    <p className="text-neutral-600 text-body-sm">
                      Étage {formatFloor(account?.floor)} · Appt{' '}
                      {account?.apartmentNumber || '-'}
                    </p>
                    {!!account?.residentType && (
                      <p className="text-neutral-600 text-body-sm">
                        {translateResidentType(account.residentType)}
                      </p>
                    )}
                    {account?.isMemberOfCondominiumCouncil && (
                      <span className="inline-flex items-center mt-2 px-2 py-1 rounded-badge text-body-xs font-medium bg-info-100 text-info-800">
                        Conseil syndical
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setResidentToDelete(resident)}
                    aria-label={`Supprimer ${resident.firstName} ${resident.lastName}`}
                    className="inline-flex items-center p-2 text-danger-600 hover:text-danger-700 hover:bg-danger-50 rounded-button transition-colors"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop Table - visible >= lg */}
        <div className="hidden lg:block overflow-x-auto bg-white rounded-card shadow-card border border-neutral-200">
          <table className="w-full table-auto">
            <thead>
              <tr>
                {[
                  'Prénom',
                  'Nom',
                  'Type',
                  'Conseil syndical',
                  'Étage',
                  'Appartement',
                  '',
                ].map((header, i) => (
                  <th
                    key={i}
                    className="px-3 py-2 text-center text-body-xs font-semibold text-white bg-neutral-700 border border-neutral-200 whitespace-nowrap"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredResidents.map((resident) => {
                const account = getAccount(resident);
                return (
                  <tr
                    key={resident.id}
                    className="hover:bg-neutral-50 transition-colors text-body-sm text-neutral-900"
                  >
                    <td className="px-3 py-2 text-center border-b border-neutral-200">
                      {resident.firstName}
                    </td>
                    <td className="px-3 py-2 text-center border-b border-neutral-200">
                      {resident.lastName}
                    </td>
                    <td className="px-3 py-2 text-center border-b border-neutral-200">
                      {account?.residentType
                        ? translateResidentType(account.residentType)
                        : '-'}
                    </td>
                    <td className="px-3 py-2 text-center border-b border-neutral-200">
                      {account?.isMemberOfCondominiumCouncil ? 'Oui' : 'Non'}
                    </td>
                    <td className="px-3 py-2 text-center border-b border-neutral-200">
                      {formatFloor(account?.floor)}
                    </td>
                    <td className="px-3 py-2 text-center border-b border-neutral-200">
                      {account?.apartmentNumber || '-'}
                    </td>
                    <td className="px-1 py-1.5 text-center border-b border-neutral-200">
                      <button
                        onClick={() => setResidentToDelete(resident)}
                        aria-label={`Supprimer ${resident.firstName} ${resident.lastName}`}
                        className="inline-flex items-center p-1 text-danger-600 hover:text-danger-700 hover:bg-danger-50 rounded-button transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </>
    );
  }, [isLoading, error, residents, filteredResidents, search]);

  return (
    <div>
      <DeletePropertyResidentModal
        propertyId={propertyId}
        resident={residentToDelete}
        onClose={() => setResidentToDelete(null)}
      />

      <div className="relative mb-6">
        <Search
          size={18}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none"
        />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un résident"
          aria-label="Rechercher un résident"
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-neutral-200 rounded-button text-body-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {content}
    </div>
  );
};

export default PropertyResidents;
